import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import Timezone from '../../commons/timezone/Timezone.js';
import * as Utils from '../../commons/utils/Utils';
import Loading from '../ui/Loading.js';
import SolicitudAdjuntosLista from './SolicitudAdjuntosLista.jsx';
import { COLOR_ESTADO, etiquetaEstado } from './solicitudEstado.js';
import { cancelarSolicitud } from './cancelarSolicitud.js';
import SolicitudRechazarModal from './SolicitudRechazarModal.jsx';
import SolicitudHistorialModal from './SolicitudHistorialModal.jsx';

const ItemCabecera = ({ titulo, children }) => (
	<div className="item-container">
		<div className="item title-item">{titulo}</div>
		<div className="item text-item">{children}</div>
	</div>
);

const Dato = ({ label, children }) => (
	<div className="col-md-4">
		<div className="form-group row">
			<label className="col-md-3 label-control col-form-label">{label}:</label>
			<div className="col-md-9 align-self-center">
				<div className="form-control-static col-form-label form-value">{children}</div>
			</div>
		</div>
	</div>
);

const SolicitudesDetalle = (props) => {
	const intl = useIntl();
	const id = props.match.params.id;
	const habilitado = ConfigBusiness.get('mantenimientos.solicitudes.habilitado') === 'true' && Security.hasPermission('SOLICITUDES_LISTAR');
	const inmovilizadoHabilitado = ConfigBusiness.get('tickets.inmovilizado.habilitado') === 'true';

	const [solicitud, setSolicitud] = useState(null);
	const [loading, setLoading] = useState(false);
	const [redirectTo, setRedirectTo] = useState(null);
	const [recargas, setRecargas] = useState(0);
	const [rechazoAbierto, setRechazoAbierto] = useState(false);
	const [historialAbierto, setHistorialAbierto] = useState(false);

	useEffect(() => {
		if (!habilitado) {
			setRedirectTo('/error');
			return undefined;
		}
		let vigente = true;
		setLoading(true);
		Utils.getData('/solicitudes/' + id)
			.then((data) => {
				if (vigente) setSolicitud(data);
			})
			.catch(() => {
				if (vigente) setRedirectTo('/solicitudes');
			})
			.finally(() => {
				if (vigente) setLoading(false);
			});

		return () => {
			vigente = false;
		};
	}, [id, recargas]);

	const handleCancelar = () => {
		cancelarSolicitud(intl, solicitud).then((cancelada) => {
			if (cancelada) setRecargas((actual) => actual + 1);
		});
	};

	if (redirectTo) return <Redirect push to={redirectTo} />;
	if (!solicitud) return loading ? <Loading /> : null;

	const gestiona = solicitud.estado === 'PENDIENTE' && Security.hasPermission('SOLICITUDES_GESTIONAR');
	const fechaHora = Timezone.getDateForClient(solicitud.fechaHora, null, 'DD/MM/YYYY HH:mm');
	const entidad = solicitud.entidad || {};

	return (
		<React.Fragment>
			{loading && <Loading />}
			<SolicitudHistorialModal solicitud={historialAbierto ? solicitud : null} onClose={() => setHistorialAbierto(false)} />
			<SolicitudRechazarModal
				solicitud={rechazoAbierto ? { id: solicitud.id, numero: solicitud.numero, dominio: entidad.dominio, solicitante: solicitud.solicitante } : null}
				onClose={() => setRechazoAbierto(false)}
				onRechazada={() => {
					setRechazoAbierto(false);
					setRecargas((actual) => actual + 1);
				}}
			/>
			<div className="form form-horizontal">
				<div className="form-body">
					<div className="card">
						<div className="card-content">
							<div className="card-body">
								<div className="row">
									<div className="col-12 col-lg align-self-center">
										<h5 className="header-card" style={{ flexWrap: 'wrap' }}>
											<ItemCabecera titulo={intl.formatMessage({ id: 'solicitudesDetalle.solicitud', defaultMessage: 'Solicitud' })}>
												{solicitud.numero}
											</ItemCabecera>
											<div className="divider"></div>
											<ItemCabecera titulo={intl.formatMessage({ id: 'solicitudesDetalle.entidad', defaultMessage: 'Entidad' })}>
												{entidad.dominio}
											</ItemCabecera>
											<div className="divider"></div>
											<ItemCabecera titulo={intl.formatMessage({ id: 'solicitudesDetalle.estado', defaultMessage: 'Estado' })}>
												<div
													style={{
														backgroundColor: COLOR_ESTADO[solicitud.estado] || '#6B6F82',
														padding: '0px 10px',
														borderRadius: '7px',
														color: 'white',
														fontSize: '16px',
														width: 'fit-content',
														whiteSpace: 'nowrap',
													}}
												>
													{etiquetaEstado(intl, solicitud.estado)}
												</div>
											</ItemCabecera>
											<div className="divider"></div>
											<ItemCabecera titulo={intl.formatMessage({ id: 'solicitudesDetalle.creada', defaultMessage: 'Creado el' })}>
												{Timezone.getDateForClient(solicitud.fechaHora, null, 'L')}
											</ItemCabecera>
										</h5>
									</div>
									<div className="col-12 col-lg-auto align-self-center mt-1 mt-lg-0">
										<div className="d-flex flex-wrap justify-content-lg-end" style={{ gap: 8 }}>
											{gestiona && (
												<button type="button" className="btn btn-fleet">
													<i className="la la-arrow-circle-right"></i>{' '}
													<FormattedMessage id="solicitudesDetalle.liberar" defaultMessage="Liberar a correctivo" />
												</button>
											)}
											{gestiona && (
												<button type="button" className="btn btn-danger" onClick={() => setRechazoAbierto(true)}>
													<i className="la la-times-circle"></i>{' '}
													<FormattedMessage id="solicitudesDetalle.rechazar" defaultMessage="Rechazar" />
												</button>
											)}
											{solicitud.puedeCancelar && (
												<button type="button" className="btn btn-outline-danger" onClick={handleCancelar}>
													<i className="la la-ban"></i>{' '}
													<FormattedMessage id="solicitudesDetalle.cancelar" defaultMessage="Cancelar solicitud" />
												</button>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="card pull-up">
						<div className="card-content">
							<div className="card-body">
								<h4 className="form-section">
									<i className="la la-info-circle"></i>
									<FormattedMessage id="solicitudesDetalle.datos" defaultMessage=" Datos Generales" />
								</h4>
								<div className="float-right ticket-buttons">
									<button
										type="button"
										className="btn btn-float-sm btn-round btn-fleet ml-1"
										title={intl.formatMessage({ id: 'solicitudesDetalle.ver_historico', defaultMessage: 'Ver Histórico' })}
										onClick={() => setHistorialAbierto(true)}
									>
										<i className="la la-history align-middle"></i>
									</button>
								</div>
								<div className="row">
									<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.entidad', defaultMessage: 'Entidad' })}>{entidad.dominio}</Dato>
									<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.marca', defaultMessage: 'Marca' })}>{entidad.marca || ''}</Dato>
									<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.modelo', defaultMessage: 'Modelo' })}>{entidad.modelo || ''}</Dato>
								</div>
								<div className="row">
									<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.base', defaultMessage: 'Base' })}>{solicitud.base || ''}</Dato>
									<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.region', defaultMessage: 'Región' })}>{solicitud.region || ''}</Dato>
									<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.solicitante', defaultMessage: 'Solicitante' })}>{solicitud.solicitante}</Dato>
								</div>
								<div className="row">
									<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.fecha_hora', defaultMessage: 'Fecha y hora' })}>{fechaHora}</Dato>
									{inmovilizadoHabilitado && solicitud.inmovilizado !== null && (
										<Dato label={intl.formatMessage({ id: 'correctivosAbm.datos_generales.inmovilizado', defaultMessage: 'Inmovilizado' })}>
											{solicitud.inmovilizado ? (
												<span className="text-danger">{intl.formatMessage({ id: 'correctivosAbm.inmovilizadoOptions.si', defaultMessage: 'SI' })}</span>
											) : (
												intl.formatMessage({ id: 'correctivosAbm.inmovilizadoOptions.no', defaultMessage: 'NO' })
											)}
										</Dato>
									)}
									{solicitud.fechaHoraTurno && (
										<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.fecha_turno', defaultMessage: 'Fecha de Turno' })}>
											{moment(solicitud.fechaHoraTurno).format('DD/MM/YYYY HH:mm')}
										</Dato>
									)}
									{solicitud.ticketId && (
										<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.ticket', defaultMessage: 'Ticket' })}>
											<a href={'/tickets/' + solicitud.ticketId + '/edit'}>{solicitud.ticketId}</a>
										</Dato>
									)}
								</div>
								<div className="row">
									<div className="col-md-12">
										<div className="form-group row">
											<label className="col-md-1 label-control col-form-label">
												{intl.formatMessage({ id: 'solicitudesDetalle.detalle', defaultMessage: 'Detalle' })}:
											</label>
											<div className="col-md-11 align-self-center">
												<div className="form-control-static col-form-label form-value" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
													{solicitud.detalle}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{solicitud.rechazo && (
						<div className="card pull-up">
							<div className="card-content">
								<div className="card-body">
									<h4 className="form-section">
										<i className="la la-times-circle"></i>
										<FormattedMessage id="solicitudesDetalle.rechazo" defaultMessage=" Rechazo" />
									</h4>
									<div className="row">
										<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.motivo_rechazo', defaultMessage: 'Motivo' })}>{solicitud.rechazo.motivo}</Dato>
										<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.rechazada_por', defaultMessage: 'Rechazada por' })}>{solicitud.rechazo.usuario}</Dato>
										<Dato label={intl.formatMessage({ id: 'solicitudesDetalle.fecha_rechazo', defaultMessage: 'Fecha de rechazo' })}>
											{Timezone.getDateForClient(solicitud.rechazo.fechaHora, null, 'DD/MM/YYYY HH:mm')}
										</Dato>
									</div>
									<div className="row">
										<div className="col-md-12">
											<div className="form-group row">
												<label className="col-md-1 label-control col-form-label">
													{intl.formatMessage({ id: 'solicitudesDetalle.comentario_rechazo', defaultMessage: 'Comentario' })}:
												</label>
												<div className="col-md-11 align-self-center">
													<div className="form-control-static col-form-label form-value" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
														{solicitud.rechazo.comentario}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}

					<div className="card pull-up">
						<div className="card-content">
							<div className="card-body">
								<h4 className="form-section">
									<i className="la la-paperclip"></i>
									<FormattedMessage id="solicitudesDetalle.adjuntos" defaultMessage=" Adjuntos ({cantidad})" values={{ cantidad: solicitud.adjuntos.length }} />
								</h4>
								{solicitud.adjuntos.length > 0 ? (
									<SolicitudAdjuntosLista adjuntos={solicitud.adjuntos} />
								) : (
									<FormattedMessage id="solicitudAdjuntosModal.vacio" defaultMessage="La solicitud no tiene adjuntos." />
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="card pull-up">
				<div className="card-content">
					<div className="card-body">
						<div className="text-cd text-right">
							<button type="button" className="btn btn-danger" onClick={() => props.history.push('/solicitudes')}>
								<i className="fa fa-times-circle"></i>
								<FormattedMessage id="solicitudesDetalle.volver" defaultMessage=" Volver" />
							</button>
						</div>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default SolicitudesDetalle;
